import { useMutation } from "@tanstack/react-query";
import supabase from "@/supabase";
import { format } from "date-fns";

interface VehicleCollectionBookingData {
  applicationId: string;
  sessionId: string;
  date: Date;
}

interface SessionBooking {
  id: number;
  session_id: string;
  application_id: number;
  attended: boolean;
  date: string;
  created_at?: string;
}

interface ScheduleResponse {
  success: boolean;
  message?: string;
  data?: {
    booking: SessionBooking;
    date: string;
  };
}

/**
 * Hook for scheduling vehicle collection sessions via cloud function
 */
export const useVehicleCollectionCloudFunctions = () => {
  // Schedule vehicle collection session
  const scheduleVehicleCollection = useMutation({
    mutationFn: async ({
      applicationId,
      sessionId,
      date,
    }: VehicleCollectionBookingData): Promise<ScheduleResponse> => {
      if (!applicationId || !sessionId || !date) {
        throw new Error("Application ID, Session ID, and Date are required");
      }

      try {
        // For timestamp (without timezone) in Postgres/Supabase, we need to format the date
        // as an ISO string but then remove the timezone part to preserve local time
        const localDateTimeString = format(date, "yyyy-MM-dd HH:mm:ss");

        const { data, error } = await supabase.functions.invoke(
          "schedule-vehicle-collection",
          {
            body: {
              applicationId,
              sessionId,
              date: localDateTimeString,
            },
          }
        );

        if (error) {
          console.error("Error scheduling vehicle collection:", error);
          try {
            // Try to parse the error response body to get the actual error message
            const parsedError = await error.context.json();
            return parsedError;
          } catch (parseError) {
            // If parsing fails, fall back to error.message or default message
            return {
              success: false,
              message: error.message || "Failed to schedule vehicle collection",
            };
          }
        }

        return data;
      } catch (error) {
        console.error("Error in scheduleVehicleCollection:", error);
        throw error;
      }
    },
  });

  return {
    scheduleVehicleCollection,
  };
};
